/**
 * AUTOMATION THUNKS — 5 thunk CRUD standard via `createCrudThunks`, piu' i
 * thunk custom per le statistiche e le transizioni di ciclo di vita
 * (pattern `updateTaskStatus`).
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Page } from '@crm/shared';
import { extractError, requireGroupId } from '../../store/thunkHelpers';
import type { RootState } from '../../store/store';
import { createCrudThunks } from '../../store/createCrudThunks';
import { automationApi, type AutomationListParams } from './automationApi';
import type {
  Automation,
  AutomationActionType,
  AutomationCategory,
  AutomationCreatePayload,
  AutomationExecution,
  AutomationObjectType,
  AutomationRun,
  AutomationStats,
  AutomationUpdatePayload,
  AutomationViolation,
  CatalogField,
} from './automationTypes';

/** Fetch-all: poche automazioni per sito, filtro/paginazione client-side. */
export const FETCH_ALL_PAGE_SIZE = 200;

const thunks = createCrudThunks<
  Automation,
  AutomationCreatePayload,
  AutomationUpdatePayload,
  AutomationListParams
>('automations', automationApi);

/**
 * Argomento dei fetch del monitoraggio: id automazione + filtro periodo opzionale
 * (`startDateFrom`/`startDateTo` ISO-8601 sullo startDate dei run; entrambi assenti = tutto).
 * <p>Non esportata: la usano i due thunk qui sotto e nessun altro. Chi li dispatcha passa un
 * oggetto letterale e il tipo lo inferisce, quindi esportarla prometterebbe una superficie che
 * nessuno consuma — e che andrebbe poi mantenuta stabile per un consumatore che non esiste.
 */
interface MonitoringFetchArg {
  automationDefinitionId: number;
  startDateFrom?: string;
  startDateTo?: string;
}

/**
 * Statistiche di esecuzione di una singola automazione (GET /automation/{id}/stats).
 * L'elenco lo dispatcha per gli id a video, popolando `statsById`.
 */
export const fetchAutomationStats = createAsyncThunk<
  AutomationStats,
  MonitoringFetchArg,
  { rejectValue: string }
>(
  'automations/fetchStats',
  async ({ automationDefinitionId, startDateFrom, startDateTo }, { rejectWithValue }) => {
    try {
      return await automationApi.getStats(automationDefinitionId, { startDateFrom, startDateTo });
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Log dei run di una singola automazione (GET /automation/{id}/runs). Il
 * monitoraggio lo dispatcha per l'automazione a video; lo slice indicizza il
 * risultato per id (action.meta.arg.automationDefinitionId) in `runsById`.
 */
export const fetchAutomationRuns = createAsyncThunk<
  Page<AutomationRun>,
  MonitoringFetchArg,
  { rejectValue: string }
>(
  'automations/fetchRuns',
  async ({ automationDefinitionId, startDateFrom, startDateTo }, { rejectWithValue }) => {
    try {
      return await automationApi.getRuns(automationDefinitionId, { startDateFrom, startDateTo });
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Esiti per record di un run (GET /automation/{id}/runs/{runId}/executions). Lo dispatcha la riga
 * del log che si apre, non la pagina: lo slice indicizza il risultato per RUN
 * (action.meta.arg.automationRunId) in `executionsByRunId`, perché è quello a identificare il
 * dettaglio — la stessa automazione ne ha uno per ogni passata.
 */
export const fetchAutomationRunExecutions = createAsyncThunk<
  Page<AutomationExecution>,
  { automationDefinitionId: number; automationRunId: number },
  { rejectValue: string }
>(
  'automations/fetchRunExecutions',
  async ({ automationDefinitionId, automationRunId }, { rejectWithValue }) => {
    try {
      return await automationApi.getRunExecutions(automationDefinitionId, automationRunId);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Cancella un run e il suo sottoalbero (DELETE /automation/{id}/runs/{runId}). Lo dispatcha
 * l'azione di riga del log nel monitoraggio.
 * <p>Restituisce l'ID CANCELLATO invece di `void`: lo slice deve togliere quella riga da
 * `runsById`, e `action.meta.arg` porterebbe la stessa informazione — ma dichiararla come payload
 * dice che è il risultato dell'operazione e non un dettaglio della richiesta, ed è ciò che il
 * reducer legge.
 * <p>Nessun refetch dopo la cancellazione: la lista a video è già quella giusta meno una riga, e
 * rifare la fetch costerebbe un giro di rete per riscoprirlo.
 */
export const deleteAutomationRun = createAsyncThunk<
  { automationDefinitionId: number; automationRunId: number },
  { automationDefinitionId: number; automationRunId: number },
  { rejectValue: string }
>(
  'automations/deleteRun',
  async ({ automationDefinitionId, automationRunId }, { rejectWithValue }) => {
    try {
      await automationApi.deleteRun(automationDefinitionId, automationRunId);

      return { automationDefinitionId, automationRunId };
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Ripulisce lo storico dalle passate senza esiti (DELETE /automation/{id}/runs). Lo dispatcha il
 * pulsante in testa al log del monitoraggio.
 * <p>Restituisce l'id dell'AUTOMAZIONE e non quelli dei run spariti, perché nessuno li conosce: il
 * criterio lo applica il server riga per riga, e la risposta non li elenca. Il reducer ne ricava la
 * sola cosa che può fare senza saperlo — buttare i drill-down in cache — e la lista giusta la
 * riporta il rifetch.
 * <p><b>Rifetch obbligato</b>, all'opposto della cancellazione singola: lì la lista a video era
 * già quella giusta meno una riga, qui il client non sa nemmeno quante righe siano sparite.
 */
export const deleteEmptyAutomationRuns = createAsyncThunk<
  { automationDefinitionId: number },
  { automationDefinitionId: number },
  { rejectValue: string }
>('automations/deleteEmptyRuns', async ({ automationDefinitionId }, { rejectWithValue }) => {
  try {
    await automationApi.deleteEmptyRuns(automationDefinitionId);

    return { automationDefinitionId };
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Categorie automazione del sito (GET /automations/categories/{groupId}). Popola `categories`
 * (tab e colori del chip nella gallery template).
 */
export const fetchAutomationCategories = createAsyncThunk<
  AutomationCategory[],
  void,
  { state: RootState; rejectValue: string }
>('automations/fetchCategories', async (_, { getState, rejectWithValue }) => {
  try {
    return await automationApi.getCategories(requireGroupId(getState));
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Template del sito (GET /automations/{groupId}?template=true). Popola `templates` (card della
 * gallery), slot separato da `items` (automazioni operative della lista).
 */
export const fetchAutomationTemplates = createAsyncThunk<
  Automation[],
  void,
  { state: RootState; rejectValue: string }
>('automations/fetchTemplates', async (_, { getState, rejectWithValue }) => {
  try {
    const page = await automationApi.getAll(requireGroupId(getState), {
      template: true,
      pageSize: FETCH_ALL_PAGE_SIZE,
    });
    return page.items;
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Catalogo QUANDO del builder (GET /automations/object-types/{groupId}). Popola `objectTypes`,
 * la sorgente delle due combo del blocco QUANDO (oggetto → eventi → parametri). Per-sito: il
 * catalogo è filtrato per il groupId attivo (un oggetto compare solo se abilitato per il sito).
 */
export const fetchAutomationObjectTypes = createAsyncThunk<
  AutomationObjectType[],
  void,
  { state: RootState; rejectValue: string }
>('automations/fetchObjectTypes', async (_, { getState, rejectWithValue }) => {
  try {
    return await automationApi.getObjectTypes(requireGroupId(getState));
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Ripristina i template predefiniti del sito (POST /automations/templates/restore/{groupId}).
 *
 * Non tocca lo store: il server cancella e ricrea, quindi l'elenco che il client ha in mano non è
 * più ricostruibile da qui — chi lo chiama rilegge con `fetchAutomationTemplates`. Aggiornare lo
 * slice a mano vorrebbe dire indovinare cosa il seeding ha creato.
 */
export const restoreAutomationTemplates = createAsyncThunk<
  void,
  void,
  { state: RootState; rejectValue: string }
>('automations/restoreTemplates', async (_, { getState, rejectWithValue }) => {
  try {
    await automationApi.restoreTemplates(requireGroupId(getState));
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Catalogo SE del builder (GET /automations/condition-types/{groupId}?className=). Popola
 * `conditionTypesByClassName[className]`, la sorgente dei campi filtrabili del blocco SE per
 * l'oggetto scelto nel QUANDO. Ritorna { className, fields } così lo slice indicizza per FQN.
 */
export const fetchAutomationConditionTypes = createAsyncThunk<
  { className: string; fields: CatalogField[] },
  string,
  { state: RootState; rejectValue: string }
>('automations/fetchConditionTypes', async (className, { getState, rejectWithValue }) => {
  try {
    const fields = await automationApi.getConditionTypes(requireGroupId(getState), className);
    return { className, fields };
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Catalogo dei campi scrivibili (GET /automations/updatable-fields/{groupId}?className=). Popola
 * `updatableFieldsByClassName[className]`, che alimenterà la configurazione delle azioni di
 * aggiornamento campo del blocco ALLORA. Stessa forma e stessa cache per FQN del catalogo SE.
 *
 * Nessuno lo dispatcha ancora: il consumatore è la UI di configurazione dell'azione, che non
 * esiste. Sta qui perché il data layer si monta prima di chi lo usa, non perché serva già.
 */
export const fetchAutomationUpdatableFields = createAsyncThunk<
  { className: string; fields: CatalogField[] },
  string,
  { state: RootState; rejectValue: string }
>('automations/fetchUpdatableFields', async (className, { getState, rejectWithValue }) => {
  try {
    const fields = await automationApi.getUpdatableFields(requireGroupId(getState), className);
    return { className, fields };
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Catalogo ALLORA del builder (GET /automations/action-types/{groupId}?className=). Popola
 * `actionTypesByClassName[className]`, la sorgente delle azioni offribili per l'oggetto scelto nel
 * QUANDO. Ritorna { className, actionTypes } così lo slice indicizza per FQN, esattamente come il
 * catalogo del SE: le azioni NON sono più globali, perché non tutte valgono per tutte le entità.
 */
export const fetchAutomationActionTypes = createAsyncThunk<
  { className: string; actionTypes: AutomationActionType[] },
  string,
  { state: RootState; rejectValue: string }
>('automations/fetchActionTypes', async (className, { getState, rejectWithValue }) => {
  try {
    const actionTypes = await automationApi.getActionTypes(requireGroupId(getState), className);
    return { className, actionTypes };
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Valida la bozza in composizione (POST /automations/{groupId}/validate) e popola `violations`:
 * alimenta il pannello Validazione del builder. Le regole NON sono qui — le esegue lo stesso
 * componente che il service interroga alla pubblicazione, quindi pannello e guardia non possono
 * divergere. Ma giudicano cose diverse: qui il PAYLOAD a video, là il RECORD in DB. Col pannello
 * verde il publish non obietta finché i due coincidono, ed è il motivo per cui Attiva pretende
 * anche che non ci siano modifiche pendenti.
 */
export const validateAutomation = createAsyncThunk<
  AutomationViolation[],
  AutomationCreatePayload,
  { state: RootState; rejectValue: string }
>('automations/validate', async (payload, { getState, rejectWithValue }) => {
  try {
    return await automationApi.validate(requireGroupId(getState), payload);
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/** Factory per i thunk di transizione (publish/suspend/resume/new-version). */
function transitionThunk(verb: string, run: (id: number) => Promise<Automation>) {
  return createAsyncThunk<Automation, number, { rejectValue: string }>(
    `automations/${verb}`,
    async (automationDefinitionId, { rejectWithValue }) => {
      try {
        return await run(automationDefinitionId);
      } catch (err) {
        return rejectWithValue(extractError(err));
      }
    },
  );
}

/**
 * Codice — non testo — con cui il backend marca il 400 della pubblicazione rifiutata per
 * validazione. Viaggia nel `title` del Problem, che per questo caso NON è leggibile: il testo per
 * l'utente sta nel `detail`, dove le violazioni sono già tradotte nel suo locale.
 */
const NOT_PUBLISHABLE_TITLE = 'the-automation-cannot-be-published';

/**
 * Testo da mostrare per un 400 di pubblicazione rifiutata; `null` se l'errore è un altro e va
 * trattato come tutti gli altri.
 * <p>Serve perché `extractError` restituisce il `title` quando c'è, e qui il title è il codice:
 * senza questa lettura l'utente vedrebbe `the-automation-cannot-be-published` a video.
 * <p>Il `detail` arriva in <b>due forme</b>, perché due sono i modi in cui il service rifiuta:
 * l'elenco delle violazioni per blocco (JSON, stessa forma del DTO di /validate) quando
 * l'automazione non sta in piedi, oppure una frase singola quando non è stato possibile giudicarla
 * — il validatore non è nel registro. Entrambe sono già tradotte nel locale dell'utente, quindi
 * quello che non è JSON si mostra così com'è: <b>il codice del title non deve mai arrivare a
 * video</b>, ed è la ragione per cui questa funzione esiste.
 * <p>Il blocco non entra nel testo: i messaggi del validatore si spiegano da soli ("aggiungi almeno
 * un'azione"), e la sigla WHEN/IF/THEN è un dettaglio del protocollo, non qualcosa da mostrare.
 * Separatore ` · `, lo stesso con cui il riepilogo del builder elenca le azioni.
 */
function extractPublishViolations(err: unknown): string | null {
  const data = (err as { response?: { data?: { title?: string; detail?: string } } })?.response
    ?.data;

  if (!data || data.title !== NOT_PUBLISHABLE_TITLE || !data.detail) return null;

  try {
    const violations = JSON.parse(data.detail) as { block?: string; message?: string }[];
    // Type predicate e non `filter(Boolean)`: quest'ultimo non restringe il tipo, e un `undefined`
    // finirebbe a video come stringa vuota fra due separatori.
    const messages = violations
      .map((violation) => violation.message)
      .filter((message): message is string => Boolean(message));

    return messages.length > 0 ? messages.join(' · ') : data.detail;
  } catch {
    // Non è l'elenco ma un messaggio singolo: già tradotto, si mostra tale e quale.
    return data.detail;
  }
}

/**
 * Pubblicazione: fuori dalla factory perché è l'unica transizione che il backend può rifiutare per
 * MERITO e non per stato — l'automazione non sta in piedi — e quel rifiuto porta con sé l'elenco
 * di cosa manca. Le altre transizioni falliscono solo sullo stato di partenza, dove il messaggio
 * generico dice già tutto.
 */
export const publishAutomation = createAsyncThunk<Automation, number, { rejectValue: string }>(
  'automations/publish',
  async (automationDefinitionId, { rejectWithValue }) => {
    try {
      return await automationApi.publish(automationDefinitionId);
    } catch (err) {
      return rejectWithValue(extractPublishViolations(err) ?? extractError(err));
    }
  },
);

export const suspendAutomation = transitionThunk('suspend', automationApi.suspend);
export const resumeAutomation = transitionThunk('resume', automationApi.resume);

/**
 * Accende o spegne una singola azione. Non passa da `transitionThunk` perché quella factory prende
 * l'id dell'automazione e nient'altro: qui gli argomenti sono due, e l'id è quello dell'AZIONE.
 * <p>Restituisce comunque l'automazione, quindi lo slice la tratta come le transizioni — stesso
 * `applyUpdated`, stessa reattività di `currentItem` senza ricaricare.
 */
export const setAutomationActionEnabled = createAsyncThunk<
  Automation,
  { automationActionId: number; enabled: boolean },
  { rejectValue: string }
>(
  'automations/setActionEnabled',
  async ({ automationActionId, enabled }, { rejectWithValue }) => {
    try {
      return await automationApi.setActionEnabled(automationActionId, enabled);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);
/**
 * Cambia quanto storico l'automazione deve tenere, in-place e su qualunque stato.
 * <p>Restituisce l'automazione aggiornata come le transizioni di ciclo di vita, e per la stessa
 * ragione: chi ha mosso lo switch sta guardando quel record, e lo slice lo rimpiazza dove si trova
 * senza che nessuno debba rileggerlo.
 */
export const setAutomationLogEveryRecord = createAsyncThunk<
  Automation,
  { automationDefinitionId: number; logEveryRecord: boolean },
  { rejectValue: string }
>(
  'automations/setLogEveryRecord',
  async ({ automationDefinitionId, logEveryRecord }, { rejectWithValue }) => {
    try {
      return await automationApi.setLogEveryRecord(automationDefinitionId, logEveryRecord);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

export const createAutomationVersion = transitionThunk('newVersion', automationApi.createVersion);

export const automationThunks = thunks;
export const {
  fetchAll: fetchAutomations,
  fetchOne: fetchAutomation,
  create: createAutomation,
  update: updateAutomation,
  remove: deleteAutomation,
} = thunks;
